import {DependencyInjectId} from '@@/package/const/DependencyInjectId';
import {type ApplicationContextInterface} from '@@/package/application/applicationContext';
import type CustomResponse from '~~/package/types/ResponseType';
import {AssessmentSummaryModel} from '~~/package/mapper/report/model/assessment/AssessmentSummaryModel';
import type ReportApi from '~~/package/apis/report/ReportApi';
import {ReportDomainType} from '~~/package/mapper/report/model/ReportDomainType';
import type {TrackSummaryModel} from '~~/package/mapper/report/model/assessment/TrackSummaryModel';
import type {SkillSummaryModel} from '~~/package/mapper/report/model/assessment/SkillSummaryModel';
import {
    AssessmentCandidateSummaryModel
} from '~~/package/mapper/report/model/candidate/AssessmentCandidateSummaryModel';
import {TrackCandidateSummaryModel} from '~~/package/mapper/report/model/candidate/TrackCandidateSummaryModel';
import {CandidateActionAnalysisModel} from '~~/package/mapper/report/model/candidate/CandidateActionAnalysisModel';

export default class ReportMapper {
    protected reportApi: ReportApi;

    constructor(context: ApplicationContextInterface) {
        this.reportApi = context.getService<ReportApi>(DependencyInjectId.ReportApi);
    }

    public async getReportAssessmentSummary(domainType: ReportDomainType, domainId: number): Promise<AssessmentSummaryModel> {
        const insightsResponse: CustomResponse<AssessmentSummaryModel> = (await this.reportApi.getReportAssessmentSummary(domainType, domainId)).data;
        return AssessmentSummaryModel.create(insightsResponse.data)
    }

    public async getReportTrackSummary(domainType: ReportDomainType, domainId: number, sort?: string): Promise<TrackSummaryModel[]> {
        const insightsResponse: CustomResponse<TrackSummaryModel[]> = (await this.reportApi.getReportTrackSummary(domainType, domainId, sort)).data;
        return insightsResponse.data
    }

    public async getReportSkillSummary(lang: string, domainType: ReportDomainType, domainId: number, sort?: string): Promise<SkillSummaryModel[]> {
        const insightsResponse: CustomResponse<SkillSummaryModel[]> = (await this.reportApi.getReportSkillSummary(lang, domainType, domainId, sort)).data;
        return insightsResponse.data
    }

    public async getReportCandidateAssessmentSummary(userUUID: string, domainType: ReportDomainType, domainId: number): Promise<AssessmentCandidateSummaryModel> {
        const insightsResponse: CustomResponse<AssessmentCandidateSummaryModel> = (await this.reportApi.getReportCandidateAssessmentSummary(userUUID, domainType, domainId)).data;
        return AssessmentCandidateSummaryModel.create(insightsResponse.data)
    }

    public async getReportCandidateTrackSummary(userUUID: string, domainType: ReportDomainType, domainId: number): Promise<TrackCandidateSummaryModel[]> {
        const insightsResponse: CustomResponse<TrackCandidateSummaryModel[]> = (await this.reportApi.getReportCandidateTrackSummary(userUUID, domainType, domainId)).data;
        return insightsResponse.data
    }

    public async getReportCandidateSkillSummary(lang: string, userUUID: string, domainType: ReportDomainType, domainId: number, sort?: string): Promise<SkillSummaryModel[]> {
        const insightsResponse: CustomResponse<SkillSummaryModel[]> = (await this.reportApi.getReportCandidateSkillSummary(lang, userUUID, domainType, domainId, sort)).data;
        return insightsResponse.data
    }

    public async getReportCandidateActionAnalysis(userUUID: string, domainType: ReportDomainType, domainId: number): Promise<CandidateActionAnalysisModel> {
        const insightsResponse: CustomResponse<CandidateActionAnalysisModel> = (await this.reportApi.getReportCandidateActionAnalysis(userUUID, domainType, domainId)).data;
        return CandidateActionAnalysisModel.create(insightsResponse.data)
    }

    public async submitReportExport(email: string, url: string, domainType: ReportDomainType, domainId: number): Promise<boolean> {
        const insightsResponse: CustomResponse<boolean> = (await this.reportApi.submitReportExport(email, url, domainType, domainId)).data;
        return insightsResponse.data
    }

    public async submitBulkReportExport(email: string, domainType: ReportDomainType, domainId: number): Promise<boolean> {
        const insightsResponse: CustomResponse<boolean> = (await this.reportApi.submitBulkReportExport(email, domainType, domainId)).data;
        return insightsResponse.data
    }
}
